import AuthComponent from "@components/auth/component";
import BackButton from "@components/buttons/BackButton";
import CustomButton from "@components/buttons/CustomButton";
import CustomInputField from "@components/form/CustomInputField";
import CustomSelectField from "@components/form/CustomSelectField";
import CustomTextAreaField from "@components/form/CustomTextAreaField";
import { LoadingSpinner } from "@components/layouts/LoadingSpinner";
import PageWrapper from "@components/layouts/PageWrapper";
import { Toast } from "@helpers/popups/Toast";
import {
  pushNotificationSchema,
  ZPushNotification,
} from "@helpers/validation-schemas/others/push-notification.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useErrorHandling } from "@hooks/useErrorHandling";
import { useCreatePushNotificationMutation } from "@states/others/push-notification.api";
import { TFailResponse } from "@type-defs/general/TFailResponse";
import { TSuccessResponse } from "@type-defs/general/TSuccessResponse";
import { PUSH_NOTIFICATION } from "@utils/constants/others/push-notification.constants";
import { castTypeArr } from "@utils/general/cast-type";
import React from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

const formatScheduledAt = (value?: string | null) => {
  if (!value) return null;
  return `${value.replace("T", " ")}:00`;
};

function PushNotificationAddPage() {
  const navigate = useNavigate();
  const [
    createPushNotification,
    { isLoading, error: createError },
  ] = useCreatePushNotificationMutation();

  const {
    register,
    handleSubmit,
    setError,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ZPushNotification>({
    resolver: zodResolver(pushNotificationSchema),
    defaultValues: {
      schedule_type: "immediate",
      status: 1,
    },
  });

  useErrorHandling(...castTypeArr<TFailResponse>([createError]));

  const scheduleType = watch("schedule_type");

  const onSubmit: SubmitHandler<ZPushNotification> = async (data) => {
    try {
      const payload = {
        title: data.title,
        message: data.message,
        schedule_type: data.schedule_type,
        scheduled_at:
          data.schedule_type === "scheduled"
            ? formatScheduledAt(data.scheduled_at)
            : null,
        status: Number(data.status ?? 1),
      };

      const res = await createPushNotification(payload);
      const successData = "data" in res ? (res.data as TSuccessResponse) : null;

      if (successData?.success) {
        await Toast.fire({
          title: successData.message,
          icon: "success",
        });

        navigate(PUSH_NOTIFICATION.BASE_PATH, {
          state: {
            reload: true,
          },
        });
      }
    } catch (error: any) {
      setError("root", {
        message: error.message,
      });
    }
  };

  return (
    <AuthComponent
      action="create"
      module="Others"
      subModule="Push Notification"
      page
    >
      <PageWrapper>
        {isLoading && <LoadingSpinner />}
        <BackButton />
        <form onSubmit={handleSubmit(onSubmit)}>
          <h3 className="mb-4 ml-2 text-2xl font-[600] uppercase text-primary-dark">
            {PUSH_NOTIFICATION.ADD_NEW_TITLE}
          </h3>

          <div className="grid grid-cols-6 gap-x-4 gap-y-8">
            <CustomInputField
              label="Title"
              errors={errors}
              name="title"
              placeHolder="Enter title"
              register={register}
              required
              colSpan={3}
            />
            <CustomSelectField
              name="status"
              label="Status"
              control={control as any}
              errors={errors}
              options={[
                { label: "Active", value: "1" },
                { label: "Inactive", value: "0" },
              ]}
              required
              colSpan={2}
            />
          </div>

          <div className="grid grid-cols-6 gap-x-4 gap-y-8">
            <CustomTextAreaField
              label="Message"
              errors={errors}
              name="message"
              placeHolder="Enter message"
              register={register}
              required
              colSpan={6}
              rows={5}
            />
          </div>

          <div className="grid grid-cols-6 gap-x-4 gap-y-8">
            <CustomSelectField
              name="schedule_type"
              label="Schedule Type"
              control={control as any}
              errors={errors}
              options={[
                { label: "Immediate", value: "immediate" },
                { label: "Scheduled", value: "scheduled" },
              ]}
              required
              colSpan={2}
            />
            {scheduleType === "scheduled" ? (
              <CustomInputField
                inputType="datetime-local"
                label="Scheduled At"
                errors={errors}
                name="scheduled_at"
                placeHolder="Select scheduled time"
                register={register}
                required
                colSpan={2}
              />
            ) : null}
          </div>

          <div className="ml-2 mt-14">
            <CustomButton
              isLarge
              title="Cancel"
              color="white"
              textColor="gray.800"
              className="mr-10"
              handleClick={() => navigate(-1)}
            />
            <CustomButton
              handleClick={handleSubmit(onSubmit)}
              isLarge
              title={isSubmitting ? "Loading..." : "Submit"}
              disabled={isSubmitting}
              type="submit"
            />
          </div>

          {errors.root ? (
            <div className="mt-6 text-red-500">{errors.root.message}</div>
          ) : null}
        </form>
      </PageWrapper>
    </AuthComponent>
  );
}

export default PushNotificationAddPage;
